import { useEffect, useRef } from 'react';
import { Check, Sparkles } from 'lucide-react';

const residenceFeatures = [
  {
    title: "Architectural Marvel",
    description: "Designed by award-winning architects, every element speaks of timeless elegance",
    features: ["7.7 Acres Site Area","5 Towers","55-story iconic tower", "655 Uber-luxury residences"]
  },
  {
    title: "Exclusive Amenities",
    description: "A lifestyle that redefines luxury living",
    features: ["Super Built-Up Area : 5250 - 7460 Sq.Ft.","1,80,000 SQ. FT. Clubhouse, Stilt & Skypark Amenities",]
  },
  {
    title: "Sky Park Amenities",
    description: "Unwind at 2597 ft. above mean sea level, amidst the clouds, at the one-of-a-kind Sky Park - with refreshing dip, your favorite flick,or a captivating read.",
    features: ["Sky Infinity Pool", "Sky Yoga", "Sky Gym", "Sky Party Lounge", "Sky Cinema", "Sky Library", "Sky Garden", "Sky Walk", "Sky Stargazing Deck"]
  },
  
];

export const ResidenceSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      const elements = sectionRef.current.querySelectorAll('.fade-in-section');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="residence" ref={sectionRef} className="relative py-32 bg-gradient-to-b from-black via-gray-dark to-black overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #c9a961 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="max-w-4xl mx-auto text-center mb-20 fade-in-section opacity-0 transition-all duration-1000">
          <div className="inline-flex items-center space-x-2 px-6 py-2 bg-gold-primary/10 border border-gold-primary/30 rounded-full mb-6">
            <img src='../image/MSN-One-Logo-Icon-01.svg' alt='Logo' className='w-5 h-5 mt-1' />
            <span className="font-sans text-sm text-gold-light tracking-widest uppercase">The Residence</span>
          </div>
          <h2 className="font-display text-6xl md:text-7xl font-bold text-white mb-6">
            ONE <span className="gradient-gold">OF A KIND</span>
            <br />DESTINATION
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-primary to-transparent mx-auto mb-6"></div>
          <p className="font-garamond text-xl text-gray-400 leading-relaxed">
            One by MSN is meticulously designed with 5 imposing towers and is made complete with a Yacht-shaped Clubhouse at the heart of the project.
          </p>
        </div>

        {/* Split Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Image Side */}
          <div className="fade-in-section opacity-0 transition-all duration-1000 delay-2">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-gold-primary/20 to-transparent rounded-2xl blur-2xl group-hover:blur-3xl transition-all duration-700"></div>
              <img
                src="../image/prop8.webp"
                alt="Luxury Interior"
                className="relative rounded-2xl shadow-2xl w-full h-[600px] object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 border-2 border-gold-primary/30 rounded-2xl group-hover:border-gold-primary/60 transition-all duration-700"></div>
            </div>
          </div>

          {/* Content Side */}
          <div className="space-y-12">
            {residenceFeatures.map((feature, index) => (
              <div
                key={index}
                className="fade-in-section opacity-0 transition-all duration-1000"
                style={{ transitionDelay: `${(index + 3) * 200}ms` }}
              >
                <div className="luxury-glass rounded-xl p-8 hover:bg-white/5 transition-all duration-500">
                  <h3 className="font-display text-3xl font-bold text-gold-primary mb-3">
                    {feature.title}
                  </h3>
                  <p className="font-sans text-gray-400 mb-6 leading-relaxed">
                    {feature.description}
                  </p>
                  <div className="space-y-3">
                    {feature.features.map((item, i) => (
                      <div key={i} className="flex items-center space-x-3 group">
                        <div className="w-6 h-6 rounded-full bg-gold-primary/20 flex items-center justify-center group-hover:bg-gold-primary/40 transition-all duration-300">
                          <Check className="w-4 h-4 text-gold-primary" />
                        </div>
                        <span className="font-sans text-gray-300 group-hover:text-white transition-colors">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .fade-in-section.animate-in {
          opacity: 1 !important;
        }
      `}</style>
    </section>
  );
};
